<template>
  <v-app-bar
    color="white"
    :elevation="0"
    :border="false"
    height="64"
    class="app-header hidden-print-only"
  >
    <template #prepend>
      <NavigationImage
        :alt="t('navigation.logo.alt-texts.1')"
        :src="logoName"
        :width="140"
        :height="36"
        class="ml-2"
      />
    </template>

    <template #append>
      <v-btn
        v-if="showLiveChatButton"
        variant="text"
        color="primary"
        rounded="lg"
        class="mr-1 header-widget-btn"
        @click="openLiveChat"
      >
        <v-icon start>mdi-chat-outline</v-icon>
        {{ t('ai-assistant.live-chat-button') }}
      </v-btn>

      <v-app-bar-nav-icon
        v-if="props.showMenuToggle && display.mdAndDown"
        color="primary"
        @click.stop="drawerToggle"
      />
    </template>
  </v-app-bar>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import logoName from '@/assets/img/logo-name.png'
import useFeature from '@/composables/useFeature'
import { useNavigationDrawer } from '@/composables/useNavigationDrawer'
import featureNames from '@/lib/featureNames'
import NavigationImage from '@/modules/navigation/NavigationImage.vue'
import { useSupportWidgetsStore } from '@/stores/supportWidgets'

const props = withDefaults(
  defineProps<{
    /** Show the hamburger menu toggle button (visible only on md and below) */
    showMenuToggle?: boolean
  }>(),
  {
    showMenuToggle: false,
  }
)

const { t } = useI18n()
const display = useDisplay()

const supportStore = useSupportWidgetsStore()
const { liveChatReady } = storeToRefs(supportStore)
const { openLiveChat } = supportStore
const { toggle: drawerToggle } = useNavigationDrawer()

const { isFeatureActive } = useFeature()

// The app-bar entry point is opt-in via feature config; the widget must also be
// loaded before we offer it (openLiveChat is a no-op until then).
const showLiveChatButton = computed(
  () => liveChatReady.value && isFeatureActive(featureNames.LIVE_CHAT_APP_BAR)
)
</script>

<style scoped>
.app-header :deep(.v-toolbar__prepend) {
  margin-inline-end: 0;
}

.header-widget-btn {
  font-weight: 500;
}
</style>
